/**
 * Pendo Analytics Service
 * Implementation following: https://support.pendo.io/hc/en-us/articles/360046272771
 */

export interface PendoVisitor {
  id: string;
  email?: string;
  full_name?: string;
  environment?: string; // Add environment field
  [key: string]: any;
}

export interface PendoAccount {
  id: string;
  name?: string;
  environment?: string; // Add environment field
  [key: string]: any;
}

interface PendoInitializeOptions {
  visitor: PendoVisitor;
  account?: PendoAccount;
  events?: {
    ready?: () => void;
  };
}

declare global {
  interface Window {
    pendo?: {
      initialize: (options: PendoInitializeOptions) => void;
      identify: (options: PendoInitializeOptions) => void;
      isReady: () => boolean;
      clearSession: () => void;
      track: (eventName: string, metadata?: Record<string, any>) => void;
    };
  }
}

class PendoService {
  private static instance: PendoService;
  private apiKey: string;
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;
  private isDevelopment: boolean;

  private constructor() {
    this.apiKey = process.env.REACT_APP_DRAWS_PENDO_API_KEY || '';
    this.isDevelopment = process.env.NODE_ENV === 'development';
    
    if (!this.apiKey) {
      // Using console for Pendo service logging is acceptable
      // eslint-disable-next-line no-console
      console.warn('Pendo API key not configured. Set REACT_APP_DRAWS_PENDO_API_KEY in .env file');
    }
  }

  /**
   * Safe logging - only logs in development mode
   */
  private log(message: string, data?: any): void {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console
      console.log(message, data);
    }
  }

  /**
   * Error logging - always logs but sanitizes in production
   */
  private logError(message: string, error?: any): void {
    // eslint-disable-next-line no-console
    console.error(message, this.isDevelopment ? error : undefined);
  }

  public static getInstance(): PendoService {
    if (!PendoService.instance) {
      PendoService.instance = new PendoService();
    }
    return PendoService.instance;
  }

  /**
   * Load Pendo install script dynamically
   * Following: Agent Installation via JavaScript approach
   */
  private async loadPendoScript(): Promise<void> {
    if (!this.apiKey) {
      throw new Error('Pendo API key is required');
    }

    if (this.isInitialized) {
      return;
    }

    // Prevent multiple initialization attempts
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = new Promise((resolve, reject) => {
      // Check if script already exists
      if (document.querySelector('script[src*="pendo.io"]')) {
        this.isInitialized = true;
        resolve();
        return;
      }

      // Create and inject Pendo script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://cdn.pendo.io/agent/static/${this.apiKey}/pendo.js`;
      
      script.onload = () => {
        this.isInitialized = true;
        this.log('✅ Pendo script loaded successfully');
        resolve();
      };
      
      script.onerror = () => {
        this.logError('❌ Failed to load Pendo script');
        this.initializationPromise = null;
        reject(new Error('Failed to load Pendo script'));
      };

      // Inject into DOM
      document.head.appendChild(script);
    });

    return this.initializationPromise;
  }

  /**
   * Wait for Pendo object to be available on window
   */
  private waitForPendo(maxAttempts = 50, interval = 100): Promise<void> {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      
      const checkPendo = () => {
        if (window.pendo) {
          resolve();
        } else if (attempts >= maxAttempts) {
          reject(new Error('Pendo object not available after maximum attempts'));
        } else {
          attempts++;
          setTimeout(checkPendo, interval);
        }
      };
      
      checkPendo();
    });
  }

  /**
   * Initialize Pendo with visitor and account data
   * This should be called ONCE after user authentication
   * 
   * @param visitor - Required visitor data (must include unique id)
   * @param account - Optional account/organization data
   */
  async initialize(visitor: PendoVisitor, account?: PendoAccount): Promise<void> {
    if (!this.apiKey) {
      // eslint-disable-next-line no-console
      console.warn('Pendo initialization skipped - no API key configured');
      return;
    }

    // Validate required visitor.id
    if (!visitor.id) {
      this.logError('Pendo initialization failed - visitor.id is required');
      return;
    }

    try {
      // Step 1: Load Pendo script
      await this.loadPendoScript();

      // Step 2: Wait for Pendo object
      await this.waitForPendo();

      // Step 3: Initialize Pendo with user/account data
      window.pendo?.initialize({
        visitor,
        account,
        events: {
          ready: () => {
            // Only log initialization in development (no sensitive data in production logs)
            this.log('✅ Pendo initialized', {
              visitor: visitor.id,
              account: account?.id || 'none'
            });
          }
        }
      });
    } catch (error) {
      this.logError('❌ Pendo initialization failed:', error);
      throw error;
    }
  }

  /**
   * Update visitor/account information
   * Use this when user data changes (e.g., profile update, org switch)
   */
  async identify(visitor: PendoVisitor, account?: PendoAccount): Promise<void> {
    if (!this.apiKey || !window.pendo) {
      return;
    }

    try {
      window.pendo.identify({
        visitor,
        account
      });
      // Only log in development to avoid exposing user/account IDs
      this.log('✅ Pendo identity updated', {
        visitor: visitor.id,
        account: account?.id
      });
    } catch (error) {
      this.logError('❌ Failed to update Pendo identity:', error);
    }
  }

  /**
   * Clear Pendo session
   * Call this on user logout to end the session
   */
  clearSession(): void {
    if (!this.apiKey || !window.pendo) {
      return;
    }

    try {
      window.pendo.clearSession();
      this.log('✅ Pendo session cleared');
    } catch (error) {
      this.logError('❌ Failed to clear Pendo session:', error);
    }
  }

  /**
   * Track custom event
   * Use this to track specific user actions or milestones
   * 
   * @param eventName - Name of the event (e.g., 'draw_created', 'file_uploaded')
   * @param metadata - Optional additional data about the event
   */
  trackEvent(eventName: string, metadata?: Record<string, any>): void {
    if (!this.apiKey || !window.pendo) {
      return;
    }

    try {
      window.pendo.track(eventName, metadata);
      // Only log event tracking in development (metadata could contain sensitive data)
      this.log('📊 Pendo event tracked:', { eventName, metadata });
    } catch (error) {
      this.logError('❌ Failed to track Pendo event:', error);
    }
  }

  /**
   * Check if Pendo is ready
   */
  isReady(): boolean {
    return !!(this.apiKey && window.pendo?.isReady?.());
  }
}

// Export singleton instance
export const pendoService = PendoService.getInstance();
